import { useEffect, useState } from 'react';
import { AlertTriangle, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';

/**
 * Banner que aparece en el Dashboard cuando el gateway de WhatsApp está
 * desconectado. Polea el /healthz público cada 60s (no requiere JWT) y
 * lee `integrations['whatsapp-gateway']`.
 *
 * Estados que mostramos como alerta:
 *  - status === 'unreachable' → el gateway no responde (PM2 caído?).
 *  - status === 'disconnected' → la sesión WhatsApp se desconectó.
 *  - status === 'failed' → el engine falló.
 *  - status === 'qr_ready' → esperando que alguien escanee el QR.
 *  - status === 'stale' → gateway reporta ready pero el heartbeat no se
 *    refresca hace > 5 min (probable Chromium zombie, ver incidente
 *    2026-05-28). El API lo computa en gateway-status.ts.
 *
 * Estados NO-alerta (silencio):
 *  - status === 'ready' → todo OK.
 *  - status === 'initializing' / 'authenticating' → transitorios, no alarman.
 *  - status === 'not_configured' → el API no tiene OPENWA_* configurado;
 *    significa que la integración WhatsApp todavía no está activada en
 *    este ambiente.
 */

interface HealthzResponse {
  integrations?: {
    'whatsapp-gateway'?: {
      alive: boolean;
      status: string;
      lastActive: string | null;
    };
  };
}

const POLL_MS = 60_000;

const ALERT_STATUSES = new Set([
  'unreachable',
  'disconnected',
  'failed',
  'qr_ready',
  'stale',
]);

function statusLabel(status: string): string {
  switch (status) {
    case 'unreachable':
      return 'no responde — verifica que el gateway esté arriba';
    case 'disconnected':
      return 'se desconectó — alguien tiene que reactivar la sesión';
    case 'failed':
      return 'falló — revisa los logs del gateway';
    case 'qr_ready':
      return 'pide un nuevo escaneo de QR';
    case 'stale':
      return 'silencioso hace > 5 min — probable que el Chromium quedó colgado, reactiva la sesión';
    default:
      return status;
  }
}

export function BotHealthBanner() {
  const [bot, setBot] = useState<HealthzResponse['integrations'] extends infer T ? (T extends { 'whatsapp-gateway'?: infer G } ? G : null) : null>(null);

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      if (document.visibilityState !== 'visible') return;
      try {
        const res = await fetch('/healthz', {
          credentials: 'omit',
          cache: 'no-store',
        });
        if (!res.ok) return;
        const json = (await res.json()) as HealthzResponse;
        if (!cancelled) {
          setBot(json.integrations?.['whatsapp-gateway'] ?? null);
        }
      } catch {
        // Silencio — un /healthz que falla ya es problema mayor, no es
        // momento de tirar toast por el bot WhatsApp.
      }
    };

    void check();
    const t = setInterval(check, POLL_MS);
    const onVis = () => {
      if (document.visibilityState === 'visible') void check();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      cancelled = true;
      clearInterval(t);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  if (!bot) return null;
  if (!ALERT_STATUSES.has(bot.status)) return null;

  const lastActive = bot.lastActive ? new Date(bot.lastActive) : null;
  const lastActiveText = lastActive
    ? `Última actividad: ${lastActive.toLocaleString('es-VE')}`
    : 'Sin actividad registrada';

  return (
    <div className="mb-6 bg-amber-50 border border-amber-200 rounded-xl p-4">
      <div className="flex items-start gap-3">
        <AlertTriangle className="text-amber-600 mt-0.5 shrink-0" size={20} />
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-amber-900">
            Bot WhatsApp: {statusLabel(bot.status)}
          </h3>
          <p className="text-xs text-amber-800 mt-1">
            Mientras el bot esté así, los mensajes que envíen al grupo no van a crear tickets nuevos
            y las respuestas del panel no van a llegar. {lastActiveText}.
          </p>
        </div>
        <Link
          to="/admin/settings/whatsapp"
          className="shrink-0 inline-flex items-center gap-1 text-xs font-medium text-amber-900 hover:underline"
        >
          Ir al panel del bot <ExternalLink size={12} />
        </Link>
      </div>
    </div>
  );
}
